import { cents, type Cents } from './money.js';

export interface ParsedMoney {
  value: Cents;
  /** True when the token carried an explicit decimal part ("12.50", "12,5"). */
  hadDecimals: boolean;
}

/**
 * Parse a money token out of raw OCR text.
 *
 * Receipts are internationally inconsistent: "1,234.56", "1.234,56", "12.5",
 * "$12.50", "12.50 RON", "(3.00)" for credits. The rule used here is that the
 * *last* separator followed by one or two digits is the decimal separator;
 * every other separator is a thousands grouper.
 *
 * Returns null when the token contains no digits or is otherwise not money.
 */
export function parseMoney(raw: string): ParsedMoney | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!/\d/.test(trimmed)) return null;

  // Leading "-", trailing "-" (common on European credit lines) and
  // parenthesised amounts all mean negative.
  const negative =
    /^-/.test(trimmed) || /-$/.test(trimmed) || /^\(.*\)$/.test(trimmed);

  const cleaned = trimmed.replace(/[^\d.,]/g, '');
  if (!/\d/.test(cleaned)) return null;
  // Anything with two adjacent separators is OCR noise, not a number.
  if (/[.,]{2,}/.test(cleaned)) return null;

  const separatorIndex = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));

  let integerPart = cleaned;
  let fractionPart = '';
  if (separatorIndex !== -1) {
    const tail = cleaned.slice(separatorIndex + 1);
    if (/^\d{1,2}$/.test(tail)) {
      integerPart = cleaned.slice(0, separatorIndex);
      fractionPart = tail;
    }
    // Otherwise the separator groups thousands and the whole token is integral.
  }

  integerPart = integerPart.replace(/[.,]/g, '');
  if (integerPart === '' && fractionPart === '') return null;

  const whole = integerPart === '' ? 0 : Number.parseInt(integerPart, 10);
  const fraction = fractionPart === '' ? 0 : Number.parseInt(fractionPart.padEnd(2, '0'), 10);
  if (!Number.isFinite(whole) || !Number.isFinite(fraction)) return null;

  const magnitude = whole * 100 + fraction;
  return {
    value: cents(negative ? -magnitude : magnitude),
    hadDecimals: fractionPart !== '',
  };
}

/** Quantity prefixes like "2x", "3 X", "2 @" that receipts put before a name. */
const QUANTITY_PREFIX = /^(\d{1,3})\s*[x×@]\s*/i;

/** Extract a leading quantity marker, returning the remaining name. */
export function parseQuantityPrefix(text: string): { qty: number; rest: string } {
  const match = QUANTITY_PREFIX.exec(text);
  if (!match?.[1]) return { qty: 1, rest: text };
  return { qty: Number.parseInt(match[1], 10), rest: text.slice(match[0].length) };
}
